import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../configuration/Request';
import {
  ResponsiveContainer, ComposedChart, CartesianGrid, XAxis, YAxis,
  Tooltip, Legend, Scatter, Line,
} from 'recharts';
import { AiOutlineDownload } from 'react-icons/ai';
import Loader from 'react-loaders';

import { survivalGroupDisplay } from './survivalGroupDisplay';

const POINT_COLOR = '#2563eb';
const TREND_COLOR = '#f97316';

const formatNumber = (value, digits = 3) => {
  const num = typeof value === 'number' ? value : Number(value);
  if (value == null || Number.isNaN(num)) return '—';
  return num.toFixed(digits);
};

const humanizeKey = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

function getSurvivalSeries(plot = {}) {
  const curves = Array.isArray(plot?.curves) ? plot.curves : [];

  if (curves.length > 0) {
    return curves
      .filter(curve => Array.isArray(curve?.points))
      .map((curve, index) => {
        const name = curve.name || curve.group || curve.label || `Series ${index + 1}`;
        const points = curve.points
          .map(point => ({
            x: Number(point.time ?? point.x ?? point[plot?.x_axis?.key ?? 'x'] ?? 0),
            y: Number(point.survival ?? point.y ?? point[plot?.y_axis?.key ?? 'y'] ?? 0),
            meta: point,
          }))
          .sort((a, b) => a.x - b.x)
          .map((point, pointIndex, allPoints) => {
            if (pointIndex === 0) return point;
            const previous = allPoints[pointIndex - 1].y;
            return { ...point, y: point.y > previous ? previous : point.y };
          });

        return { name, group: curve.group || name, points };
      });
  }

  const grouped = new Map();
  (Array.isArray(plot?.points) ? plot.points : []).forEach(point => {
    const group = point.group ?? point.stratum ?? 'Survival';
    if (!grouped.has(group)) grouped.set(group, []);
    grouped.get(group).push({
      x: Number(point.time ?? point.x ?? point[plot?.x_axis?.key ?? 'x'] ?? 0),
      y: Number(point.survival ?? point.y ?? point[plot?.y_axis?.key ?? 'y'] ?? 0),
      meta: point,
    });
  });

  return [...grouped.entries()].map(([name, points]) => ({
    name,
    points: points.sort((a, b) => a.x - b.x).map((point, pointIndex, allPoints) => {
      if (pointIndex === 0) return point;
      const previous = allPoints[pointIndex - 1].y;
      return { ...point, y: point.y > previous ? previous : point.y };
    }),
  }));
}

function ChartTooltip({ active, payload, xLabel, yLabel, xKey, yKey }) {
  if (!active || !payload || !payload.length) return null;
  const meta = payload[0]?.payload?.meta || {};

  const extraEntries = Object.entries(meta).filter(
    ([key]) => key !== xKey && key !== yKey && key !== 'tnm' && key !== 'sample_count'
  );

  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px',
      padding: '10px 12px', fontSize: '0.74rem', color: '#333',
      boxShadow: '0 8px 20px rgba(15,23,42,0.14)', lineHeight: 1.6,
    }}>
      <div><strong>{xLabel}:</strong> {formatNumber(meta[xKey])}</div>
      <div><strong>{yLabel}:</strong> {formatNumber(meta[yKey])}</div>
      {meta.tnm != null && (
        <div><strong>TNM:</strong> {typeof meta.tnm === 'object' ? JSON.stringify(meta.tnm) : meta.tnm}</div>
      )}
      {meta.sample_count != null && (
        <div><strong>Sample Count:</strong> {typeof meta.sample_count === 'object' ? JSON.stringify(meta.sample_count) : meta.sample_count}</div>
      )}
      {extraEntries.map(([key, value]) => (
        <div key={key}>
          <strong>{humanizeKey(key)}:</strong> {typeof value === 'object' && value !== null ? JSON.stringify(value) : formatNumber(value)}
        </div>
      ))}
    </div>
  );
}

export function GenePlotChart({ plot, containerRef, survival = false, dataType, scatterLabel = 'Samples' }) {
  const xKey = plot?.x_axis?.key;
  const yKey = plot?.y_axis?.key;
  const xLabel = plot?.x_axis?.label || xKey || 'X';
  const yLabel = plot?.y_axis?.label || yKey || 'Y';
  const survivalSeries = survival ? getSurvivalSeries(plot).map((series, index) => ({
    ...series, ...survivalGroupDisplay(dataType, series.group || series.name, index),
  })) : [];
  const points = survival ? survivalSeries.flatMap(series => series.points) : (plot?.points || []);

  const scatterData = points.map(pt => ({ x: pt[xKey] ?? pt.x ?? pt.time, y: pt[yKey] ?? pt.y ?? pt.survival, meta: pt }));
  const xs = scatterData.map(d => d.x).filter(v => typeof v === 'number');
  const regression = plot?.regression;

  let trendData = [];
  if (regression && xs.length > 0) {
    const xMin = Math.min(...xs);
    const xMax = Math.max(...xs);
    trendData = [
      { x: xMin, y: regression.slope * xMin + regression.intercept },
      { x: xMax, y: regression.slope * xMax + regression.intercept },
    ];
  }

  if (points.length === 0) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#aaa', fontSize: '0.75rem' }}>
        No chart data available
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ padding: '6px 8px' }}>
      <ResponsiveContainer width="100%" height={340}>
        <ComposedChart margin={{ top: 12, right: 24, bottom: 30, left: 30 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis
            type="number" dataKey="x" name={xLabel}
            tick={{ fontSize: 11 }} padding={{ left: 12, right: 12 }}
            label={{ value: xLabel, position: 'insideBottom', offset: -6, fontSize: 12 }}
          />
          <YAxis
            type="number" dataKey="y" name={yLabel}
            tick={{ fontSize: 11 }} padding={{ top: 12, bottom: 12 }}
            width={66}
            label={{ value: yLabel, angle: -90, position: 'insideLeft', offset: 14, fontSize: 12, style: { textAnchor: 'middle' } }}
          />
          <Tooltip content={<ChartTooltip xLabel={xLabel} yLabel={yLabel} xKey={xKey} yKey={yKey} />} />
          <Legend wrapperStyle={{ fontSize: '0.72rem', paddingTop: '12px' }} verticalAlign="bottom" />
          {survival ? survivalSeries.map((series) => (
            <Line key={series.name} name={series.label} type="stepAfter" data={series.points.map(point => ({ x: point.x, y: point.y, meta: point.meta }))} dataKey="y" stroke={series.color} strokeWidth={2} dot={false} isAnimationActive={false} />
          )) : <Scatter name={scatterLabel} data={scatterData} fill={POINT_COLOR} />}
          {!survival && trendData.length > 0 && (
            <Line
              name="Trend"
              data={trendData}
              dataKey="y"
              stroke={TREND_COLOR}
              strokeWidth={2}
              strokeDasharray="5 4"
              dot={false}
              activeDot={false}
              legendType="line"
              isAnimationActive={false}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export function PlotDataTable({ plot }) {
  const xKey = plot?.x_axis?.key;
  const yKey = plot?.y_axis?.key;
  const xLabel = plot?.x_axis?.label || xKey || 'X';
  const yLabel = plot?.y_axis?.label || yKey || 'Y';
  const points = plot?.points || [];

  return (
    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #eef0f4', borderRadius: '12px', marginTop: '10px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
        <thead>
          <tr>
            <th style={{ padding: '6px 10px', textAlign: 'left', position: 'sticky', top: 0, background: '#f7f9fc', borderBottom: '1px solid #eef0f4' }}>{xLabel}</th>
            <th style={{ padding: '6px 10px', textAlign: 'left', position: 'sticky', top: 0, background: '#f7f9fc', borderBottom: '1px solid #eef0f4' }}>{yLabel}</th>
          </tr>
        </thead>
        <tbody>
          {points.map((pt, i) => (
            <tr key={i}>
              <td style={{ padding: '4px 10px', borderBottom: '1px solid #f4f5f9' }}>{formatNumber(pt[xKey])}</td>
              <td style={{ padding: '4px 10px', borderBottom: '1px solid #f4f5f9' }}>{formatNumber(pt[yKey])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function downloadSvgAsPng(svgEl, filename) {
  if (!svgEl) return;
  const svgClone = svgEl.cloneNode(true);
  svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  const rect = svgEl.getBoundingClientRect();
  const width = rect.width || 600;
  const height = rect.height || 300;
  svgClone.setAttribute('width', width);
  svgClone.setAttribute('height', height);

  const svgString = new XMLSerializer().serializeToString(svgClone);
  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  const img = new Image();
  img.onload = () => {
    const scale = 2;
    const canvas = document.createElement('canvas');
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(scale, scale);
    ctx.drawImage(img, 0, 0, width, height);
    URL.revokeObjectURL(url);

    canvas.toBlob((blob) => {
      const pngUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = pngUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(pngUrl);
    }, 'image/png');
  };
  img.src = url;
}

function PlotSection({ plot: plotMeta, geneName, lambdaType, context, subjectLabel, showHeader }) {
  const [chartState, setChartState] = useState({ status: 'loading' });
  const [showData, setShowData] = useState(false);
  const containerRef = useRef(null);
  const { treatment, SurgRad, analysisType } = context || {};

  useEffect(() => {
    let cancelled = false;
    setChartState({ status: 'loading' });

    axiosInstance
      .post(`${process.env.REACT_APP_BASE_URL}api/gene-plots/invoke`, {
        lambda_type: lambdaType,
        data: lambdaType === 'prognosis'
          ? {
            dataLevel: plotMeta.dataLevel,
            geneName: geneName,
            histological_subtype: plotMeta.histological_subtype,
          }
          : {
            geneName,
            histological_subtype: plotMeta.histological_subtype,
            dataLevel: plotMeta.dataLevel,
            ...(lambdaType === 'treatment' ? { treatment, SurgRad, analysisType } : {}),
            ...(lambdaType === 'cohorts' && plotMeta.coxType ? { coxType: plotMeta.coxType } : {}),
          },
      })
      .then((res) => {
        if (cancelled) return;
        if (!res.data?.success) {
          setChartState({ status: 'error' });
          return;
        }
        // normalize a few possible response shapes so the UI is resilient
        const payload = res.data?.data ?? res.data ?? {};
        const plot = payload.plot || payload.plots?.[0] || payload.data?.plot || (Array.isArray(payload) ? payload[0]?.plot : undefined) || {};
        const metadata = payload.metadata || payload.meta || payload.data?.metadata || payload.data?.meta || {};
        setChartState({ status: 'success', plot, metadata });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Chart fetch failed:', err);
        setChartState({ status: 'error' });
      });

    return () => { cancelled = true; };
  }, [geneName, plotMeta.histological_subtype, plotMeta.dataLevel, plotMeta.coxType, lambdaType, treatment, SurgRad, analysisType]);

  const estimate = Number(plotMeta.estimate);
  const isPositive = estimate > 0;
  const yLabel = chartState.plot?.y_axis?.label || 'the measured outcome';
  const samples = chartState.status === 'success' ? (chartState.plot?.points?.length ?? '—') : '—';
  const correlation = chartState.status === 'success' ? formatNumber(chartState.plot?.regression?.r) : '—';

  const handleDownload = () => {
    const svgEl = containerRef.current ? containerRef.current.querySelector('svg') : null;
    downloadSvgAsPng(svgEl, `${geneName}_${treatment || lambdaType}_plot.png`);
  };

  return (
    <div style={{ marginBottom: '24px', paddingBottom: '20px', borderBottom: '1px solid #f0f1f6' }}>
      {showHeader && (
        <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#333', marginBottom: '10px' }}>
          {[plotMeta.histological_subtype, plotMeta.dataLevel].filter(Boolean).join(' · ')}
        </div>
      )}

      {!Number.isNaN(estimate) && (
        <div className={`tp-assoc-banner ${isPositive ? 'tp-assoc-positive' : 'tp-assoc-negative'}`}>
          {geneName} expression is associated with {isPositive ? 'higher' : 'lower'} {yLabel.toLowerCase()}{subjectLabel ? ` for ${subjectLabel}` : ''} in this cohort.
        </div>
      )}

      <div className="tp-stat-grid">
        <div className="tp-stat-box">
          <div className="tp-stat-label">Effect estimate</div>
          <div className="tp-stat-value">{formatNumber(plotMeta.estimate)}</div>
        </div>
        <div className="tp-stat-box">
          <div className="tp-stat-label">p-value</div>
          <div className="tp-stat-value">{formatNumber(plotMeta.p_value, 4)}</div>
        </div>
        <div className="tp-stat-box">
          <div className="tp-stat-label">Correlation</div>
          <div className="tp-stat-value">{correlation}</div>
        </div>
        <div className="tp-stat-box">
          <div className="tp-stat-label">Samples</div>
          <div className="tp-stat-value">{samples}</div>
        </div>
      </div>

      <div className="bmt-plot-card">
        <div className="bmt-plot-header">
          {chartState.plot?.title || `${geneName} expression vs ${yLabel}`}
        </div>

        {chartState.status === 'loading' && (
          <div style={{ height: '340px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader type="ball-pulse-rise" />
          </div>
        )}
        {chartState.status === 'error' && (
          <div style={{ padding: '64px 24px', textAlign: 'center', color: '#aaa', fontSize: '0.78rem' }}>
            Chart data unavailable
          </div>
        )}
        {chartState.status === 'success' && (
          <GenePlotChart plot={chartState.plot} containerRef={containerRef} />
        )}
      </div>

      {chartState.status === 'success' && (
        <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '8px' }}>
          {[plotMeta.dataLevel, plotMeta.cancer_type].filter(Boolean).join(' · ')}
        </div>
      )}

      <div className="bmt-drawer-btn-row">
        <button
          type="button"
          className="bmt-drawer-btn bmt-drawer-btn-outline"
          onClick={() => setShowData(prev => !prev)}
          disabled={chartState.status !== 'success'}
        >
          {showData ? 'Hide Data' : 'View Data'}
        </button>
        <button
          type="button"
          className="bmt-drawer-btn bmt-drawer-btn-solid"
          onClick={handleDownload}
          disabled={chartState.status !== 'success'}
        >
          <AiOutlineDownload size={15} style={{ marginRight: '6px', verticalAlign: '-2px' }} />
          Download Plot
        </button>
      </div>

      {showData && chartState.status === 'success' && (
        <PlotDataTable plot={chartState.plot} />
      )}
    </div>
  );
}

function GeneDetailPanel({ geneName, plots, onClose, lambdaType = 'treatment', context = {} }) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setIsOpen(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    window.setTimeout(() => onClose && onClose(), 280);
  };

  const isTreatment = lambdaType === 'treatment';

  const treatmentLabel = (context?.treatment || '')
    .replace(/\./g, ' ')
    .replace(/\+/g, ' + ')
    .replace(/\s+/g, ' ')
    .trim();

  const badgeLabel = isTreatment
    ? (treatmentLabel ? `Treatment: ${treatmentLabel}` : '')
    : (plots?.[0]?.category || '');
  const subjectLabel = isTreatment ? (treatmentLabel || 'this treatment') : '';

  return (
    <>
      <div className="bmt-drawer-backdrop" onClick={handleClose} />
      <div className={`bmt-drawer${isOpen ? ' bmt-drawer-open' : ''}`} role="dialog" aria-label={`${geneName} gene details`}>
        <div className="bmt-drawer-header">
          <button type="button" className="bmt-drawer-back" onClick={handleClose}>
            ← Gene details
          </button>
          <button type="button" className="bmt-drawer-close" onClick={handleClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="bmt-drawer-body">
          <h3 className="bmt-drawer-gene">{geneName}</h3>

          {badgeLabel && (
            <div style={{ marginBottom: '16px' }}>
              <span className="tp-badge" style={{ color: '#545cd8', background: 'rgba(84,92,216,0.1)' }}>
                {badgeLabel}
              </span>
            </div>
          )}

          {(!plots || plots.length === 0) ? (
            <div style={{ padding: '24px', textAlign: 'center', color: '#888', fontSize: '0.82rem' }}>
              No plot data available for this selection.
            </div>
          ) : (
            plots.map((p, index) => (
              <PlotSection
                key={p.id ?? index}
                plot={p}
                geneName={geneName}
                lambdaType={lambdaType}
                context={context}
                subjectLabel={subjectLabel}
                showHeader={plots.length > 1}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
}

export default GeneDetailPanel;
